/**
 * Semantic color tokens for light and dark modes.
 *
 * Each token is a CSS custom property name mapped to light/dark values.
 * Injected as CSS variables on <html> via GlobalStyles (see App.tsx).
 *
 * Usage anywhere: `var(--bg-surface)`, `var(--text-primary)`, etc.
 *
 * Dark mode palette: warm browns throughout — aged leather / parchment by candlelight.
 * Base tones derived from the paper gradient (#2a2318) so chrome and content feel unified.
 */

import { READER_PAPER_BLOOM_URL_LIGHT, READER_PAPER_GRAIN_URL_LIGHT } from './paperBackground'

export interface ThemeTokens {
  [key: string]: { light: string; dark: string }
}

export const themeTokens: ThemeTokens = {
  // ── UI Chrome ──────────────────────────────────────────────
  '--bg-app': { light: '#f8f9fa', dark: '#1b1c1d' },
  '--bg-surface': { light: '#fff', dark: '#1e1f22' },
  '--bg-surface-alt': { light: '#f8f9fa', dark: '#2b2d30' },
  '--bg-hover': { light: 'rgba(0, 0, 0, 0.04)', dark: 'rgba(255, 255, 255, 0.06)' },
  '--text-primary': { light: '#202124', dark: '#f5f7fb' },
  '--text-secondary': { light: '#5f6368', dark: '#a6adbb' },
  '--text-tertiary': { light: '#999', dark: '#7f8899' },
  '--border-primary': { light: '#e0e0e0', dark: '#37383d' },
  '--border-subtle': { light: '#e8eaed', dark: '#2f3034' },
  '--floating-panel-ring': { light: 'rgba(255, 255, 255, 0.72)', dark: 'rgba(255, 255, 255, 0.14)' },
  '--floating-panel-shadow': {
    light: '0 18px 42px rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(255, 255, 255, 0.55)',
    dark: '0 28px 68px rgba(0, 0, 0, 0.58), 0 0 0 1px rgba(255, 255, 255, 0.08), 0 0 0 6px rgba(0, 0, 0, 0.2)',
  },
  '--search-result-divider': { light: '#dde2e8', dark: 'rgba(255, 255, 255, 0.18)' },
  '--icon-default': { light: '#5f6368', dark: '#b2bac9' },
  '--icon-hover': { light: '#424242', dark: '#ffffff' },
  '--shadow-color': { light: 'rgba(0, 0, 0, 0.15)', dark: 'rgba(0, 0, 0, 0.4)' },

  // ── Reader Paper ───────────────────────────────────────────
  // Light: warm cream watercolor paper. Dark: aged leather / warm dark brown.
  '--paper-bg-start': { light: '#faf6f0', dark: '#1e1f22' },
  '--paper-bg-mid': { light: '#f5efe6', dark: '#202125' },
  '--paper-bg-end': { light: '#f2ebe1', dark: '#222327' },
  '--paper-texture-opacity': { light: '0.4', dark: '0.15' },
  '--reader-page-bg': { light: '#f4ead5', dark: '#2b2d30' },
  // Sidebar/TOC — deep muted sage, the classic book-cover companion to warm
  // cream watercolor paper (Penguin Classics / Folio Society binding feel).
  // Solid, no texture: reads as the binding/cover beside the textured page.
  // Light-on-dark text overrides applied locally in the sidebar element.
  '--reader-sidebar-bg': { light: '#4a5b48', dark: '#1e2520' },
  // Reader paper has only one mode (warm cream) — dark values mirror light.
  // Two layers: microscopic grain + water-stain blooms.
  '--reader-paper-grain': {
    light: READER_PAPER_GRAIN_URL_LIGHT,
    dark: READER_PAPER_GRAIN_URL_LIGHT,
  },
  '--reader-paper-bloom': {
    light: READER_PAPER_BLOOM_URL_LIGHT,
    dark: READER_PAPER_BLOOM_URL_LIGHT,
  },
  '--reader-text-primary': { light: '#3a2e22', dark: '#e7ebf3' },
  '--reader-text-secondary': { light: '#5a4836', dark: '#bec5d3' },
  '--reader-text-muted': { light: '#857260', dark: '#97a0b2' },
  '--reader-text-submuted': { light: '#a08e76', dark: '#7f8899' },
  '--reader-heading-active': { light: '#3a2e22', dark: '#ffffff' },
  '--reader-heading-inactive': { light: '#857260', dark: '#97a0b2' },
  '--reader-sidebar-divider': { light: 'rgba(139, 115, 85, 0.15)', dark: 'rgba(255, 255, 255, 0.1)' },
  '--reader-scrollbar-main': { light: 'rgba(139, 115, 85, 0.15)', dark: 'rgba(255, 255, 255, 0.1)' },
  '--reader-scrollbar-track': { light: 'rgba(139, 115, 85, 0.03)', dark: 'rgba(255, 255, 255, 0.02)' },
  '--reader-hover': { light: 'rgba(139, 115, 85, 0.06)', dark: 'rgba(255, 255, 255, 0.08)' },
  '--reader-hover-sub': { light: 'rgba(139, 115, 85, 0.04)', dark: 'rgba(255, 255, 255, 0.06)' },
  '--reader-spinner': { light: '#b8a088', dark: '#dbe1ef' },
  '--reader-paper-shadow': {
    light: '0 1px 8px rgba(139, 115, 85, 0.08)',
    dark: '0 1px 8px rgba(0, 0, 0, 0.3)',
  },
  '--reader-star-glow': { light: 'rgba(200, 180, 150, 0.15)', dark: 'rgba(200, 180, 150, 0.08)' },

  // ── Ornament SVG colors ────────────────────────────────────
  '--ornament-fill': { light: '#b8a088', dark: '#8f98ab' },
  '--ornament-stroke': { light: '#c4b59d', dark: '#7f8899' },

  // ── Editor Content ─────────────────────────────────────────
  '--editor-text': { light: '#202124', dark: '#f5f7fb' },
  '--editor-quote-border': { light: '#e0e0e0', dark: '#495166' },
  '--editor-quote-text': { light: '#666', dark: '#a6adbb' },
  '--editor-code-bg': { light: '#f5f5f5', dark: '#2a3040' },
  '--editor-note-bg': { light: 'rgba(95, 99, 104, 0.07)', dark: 'rgba(166, 173, 187, 0.08)' },
  '--editor-note-border': { light: '#a0a5ab', dark: '#555c68' },
  '--editor-request-bg': { light: 'rgba(174, 128, 58, 0.07)', dark: 'rgba(196, 158, 97, 0.09)' },
  '--editor-request-border': { light: '#b4a185', dark: '#78664b' },
  '--editor-link': { light: '#1976d2', dark: '#7cb8e4' },
  '--editor-divider': { light: '#e0e0e0', dark: '#495166' },
  '--editor-section-divider-text': { light: '#888', dark: '#97a0b2' },

  // ── Encryption ─────────────────────────────────────────────
  '--encrypt-marked-bg': { light: '#fff3e0', dark: '#3d2e10' },
  '--encrypt-marked-border': { light: '#ff9800', dark: '#e68a00' },
  '--encrypt-marked-hover-bg': { light: '#ffe0b2', dark: '#4d3a15' },
  '--encrypt-marked-hover-border': { light: '#f57c00', dark: '#cc7a00' },
  '--encrypt-marked-badge': { light: '#ff9800', dark: '#e68a00' },
  '--encrypt-locked-bg': { light: '#e3f2fd', dark: '#1e2a30' },
  '--encrypt-locked-border': { light: '#1976d2', dark: '#5a9ec4' },
  '--encrypt-locked-badge': { light: '#1976d2', dark: '#5a9ec4' },
  '--encrypt-unlocked-bg': { light: '#e8f5e8', dark: '#1e3020' },
  '--encrypt-unlocked-border': { light: '#4caf50', dark: '#388e3c' },
  '--encrypt-publish-bg': { light: '#f3e5f5', dark: '#2d1e30' },
  '--encrypt-publish-border': { light: '#9c27b0', dark: '#7b1fa2' },
  '--encrypt-publish-badge': { light: '#9c27b0', dark: '#7b1fa2' },

  // ── Content history diff ───────────────────────────────────
  // Tinted backgrounds rather than colored text: the diff is read as prose, and
  // recoloring Hebrew body text hurts legibility more than a wash behind it does.
  '--diff-insert-bg': { light: '#dcf3e4', dark: 'rgba(74, 222, 128, 0.18)' },
  '--diff-insert-border': { light: '#4caf50', dark: '#57a86a' },
  '--diff-delete-bg': { light: '#fbe0dd', dark: 'rgba(248, 113, 113, 0.18)' },
  '--diff-delete-border': { light: '#d32f2f', dark: '#c96a68' },
  '--diff-delete-text': { light: '#8a2b23', dark: '#e8b4b0' },
  '--diff-changed-bar': { light: '#c8a415', dark: '#d3b048' },
  '--diff-moved-bg': { light: '#e8eefc', dark: 'rgba(125, 160, 240, 0.16)' },
  '--diff-moved-border': { light: '#5677c4', dark: '#7d94d0' },

  // ── Search ─────────────────────────────────────────────────
  '--search-highlight': { light: '#ffe7a3', dark: 'rgba(255, 210, 102, 0.72)' },
  '--search-highlight-text': { light: '#4f3b00', dark: '#f5f7fb' },

  // ── Sidebar / Editor layout ────────────────────────────────
  '--sidebar-bg': { light: '#fff', dark: '#1e1f22' },
  '--sidebar-selected': { light: '#E3F2FD', dark: 'rgba(255, 255, 255, 0.1)' },
  '--sidebar-selected-text': { light: '#1565C0', dark: '#ffffff' },
  '--sidebar-text': { light: '#444746', dark: '#d8deea' },
  '--sidebar-text-muted': { light: '#bbb', dark: '#56607a' },
  '--sidebar-text-muted-hover': { light: '#888', dark: '#a6adbb' },
  '--sidebar-drag-hover': { light: 'rgba(0, 0, 0, 0.04)', dark: 'rgba(255, 255, 255, 0.08)' },
  '--sidebar-heading-active': { light: '#1565C0', dark: '#ffffff' },

  // ── Toolbar ────────────────────────────────────────────────
  '--toolbar-bg': { light: '#f8f9fa', dark: '#25262a' },
  '--toolbar-border': { light: '#e0e0e0', dark: '#34353a' },
  '--toolbar-page-border': { light: '#dadce0', dark: '#34353a' },

  // ── Global scrollbars ──────────────────────────────────────
  '--scrollbar-track': { light: 'rgba(0, 0, 0, 0.06)', dark: '#25262a' },
  '--scrollbar-thumb': { light: 'rgba(0, 0, 0, 0.28)', dark: '#4b4f56' },
  '--scrollbar-thumb-hover': { light: 'rgba(0, 0, 0, 0.38)', dark: '#5c6169' },

  // ── Branch selector ────────────────────────────────────────
  '--branch-main-text': { light: '#d32f2f', dark: '#ef5350' },

  // ── Modals / Overlays ──────────────────────────────────────
  '--modal-bg': { light: '#f5f5f5', dark: '#1e1f22' },
  '--modal-border': { light: '#ddd', dark: '#3b4253' },
  '--modal-text': { light: '#333', dark: '#f5f7fb' },
  '--modal-text-secondary': { light: '#666', dark: '#a6adbb' },

  // ── Debug panel ────────────────────────────────────────────
  '--debug-bg': { light: '#f6f8fa', dark: '#1e1f22' },
  '--debug-border': { light: '#e1e4e8', dark: '#343b4a' },
  '--debug-text': { light: '#586069', dark: '#a6adbb' },
  '--debug-pre-text': { light: '#24292e', dark: '#f5f7fb' },
  '--debug-btn-bg': { light: 'rgba(25, 118, 210, 0.08)', dark: 'rgba(255, 255, 255, 0.08)' },
  '--debug-btn-hover': { light: 'rgba(25, 118, 210, 0.12)', dark: 'rgba(255, 255, 255, 0.14)' },

  // ── Context menu ───────────────────────────────────────────
  '--menu-bg': { light: '#fff', dark: '#1e1f22' },
  '--menu-border': { light: '#E0E0E0', dark: '#3b4253' },
  '--menu-shadow': { light: 'rgba(0, 0, 0, 0.15)', dark: 'rgba(0, 0, 0, 0.4)' },
  '--menu-icon': { light: '#757575', dark: '#a6adbb' },
  '--menu-icon-hover': { light: '#424242', dark: '#ffffff' },
}

/** Build a flat Record mapping `--token` → value for one mode. */
export function resolveTokens(mode: 'light' | 'dark'): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [key, val] of Object.entries(themeTokens)) {
    result[key] = val[mode]
  }
  return result
}
